<script lang="ts" generics="TCommitReceipt = undefined">
  import { onMount, onDestroy } from "svelte"
  import TableStepLayout from "@kontextwerk/web-sdk/ui/layout/TableStepLayout.svelte"
  import BatchFilterBar from "../batch-filter-bar"
  import ContactOrderPlanningActions from "./_modules/contact-order-planning-actions"
  import CostBenefitNumbersBlock from "../cost-benefit-numbers-block"
  import ContactOrderPreconfigurationPanel from "./_modules/contact-order-preconfiguration-panel"
  import ContactOrderPlanningTable from "./_modules/contact-order-planning-table"
  import {
    createContactOrderPlanningController,
    type ContactOrderPlanningProps,
  } from "./_logic/planningController.svelte"
  import { openPostalRecipientEditor } from "./_actions/openPlanningEditors"
  import {
    openRecipientQuickEdit,
    reloadRecipientQuickEditWorkOrders,
  } from "./recipient-quick-edit-commands"
  import { CONTACT_ORDER_APPOINTMENT_COLUMNS } from "./_logic/tableState"
  import { pruneContactOrderPlanningSelectedIds } from "./_logic/rowEligibility"
  import { buildContactOrderBatchPayloads } from "../../_logic/contact-orders-payload"
  const {
    organizationId,
    planningDraft = undefined,
    events,
    workOrders,
    properties,
    customers,
    contactPoints,
    employeeOptions,
    contactOrders = [],
    caseIdByOrderId = {},
    initialFilterStatus = "pending",
    rowPresentationByEventId = {},
    initialRemovedEventIds = [],
    allowRemoveOnlySubmission = false,
    locked = false,
    readOnly = false,
    createButtonLabel = "Ankündigungspläne anlegen",
    continueButtonLabel = "Überspringen",
    backButtonLabel = "Zurück",
    showContinueButton = true,
    helpModalTextId = "",
    successMessage = (createdCount) => `Ankündigungspläne angelegt: ${createdCount}.`,
    buildPayloads = buildContactOrderBatchPayloads,
    operations,
    loading = false,
    onSubmissionResult,
    onRowsRemoved = undefined,
    onRowsRestored = undefined,
    onBack = undefined,
    setError = () => {},
  }: ContactOrderPlanningProps<TCommitReceipt> = $props()

  const controller = createContactOrderPlanningController(
    () => ({
      organizationId,
      planningDraft,
      events,
      workOrders,
      properties,
      customers,
      contactPoints,
      employeeOptions,
      contactOrders,
      caseIdByOrderId,
      initialFilterStatus,
      rowPresentationByEventId,
      initialRemovedEventIds,
      allowRemoveOnlySubmission,
      locked,
      readOnly,
      createButtonLabel,
      continueButtonLabel,
      backButtonLabel,
      showContinueButton,
      helpModalTextId,
      successMessage,
      buildPayloads,
      operations,
      loading,
      onSubmissionResult,
      onRowsRemoved,
      onRowsRestored,
      onBack,
      setError,
    }),
    {
      openPostalRecipientEditor,
      openRecipientQuickEdit,
      reloadRecipientWorkOrders: reloadRecipientQuickEditWorkOrders,
    },
  )
  onMount(controller.mount)
  onDestroy(controller.destroy)
</script>

<div class="wrapper">
  <ContactOrderPlanningActions
    loading={controller.tableLoading || controller.preconfigurationPending}
    phase={controller.submissionPhase}
    createIntent={controller.createIntent}
    continuationResult={controller.buildContinueResult(false)}
    {operations}
    {showContinueButton}
    {backButtonLabel}
    {continueButtonLabel}
    {onBack}
    onPhaseChange={(phase) => (controller.submissionPhase = phase)}
    onSubmissionResult={controller.handleSubmissionResult}
  >
    {#snippet children(primaryAction, footerActions)}
      <TableStepLayout
        titleActions={readOnly ? null : primaryAction}
        title="Terminankündigung"
        count={controller.tableLoading
          ? controller.appointmentRows.length
          : controller.visibleAppointmentRows.filter((row) => !row.removed).length}
        darkMode={true}
        showPreconfiguration={!controller.contactStepLocked}
        showMetrics={controller.showMetrics}
        showFooter={!readOnly}
        {helpModalTextId}
      >
        {#snippet preconfiguration()}
          <ContactOrderPreconfigurationPanel
            selectedCount={controller.actionableSelectedIds.length}
            targetCount={controller.applyTargetResolution.targetRows.length}
            pending={controller.submissionPending}
            disabled={controller.tableLoading ||
              controller.contactStepLocked ||
              controller.submissionPending}
            digitalPreset={controller.digitalPreset}
            digitalChannelChanges={controller.digitalChannelChanges}
            postalPreset={controller.postalPreset}
            onApply={controller.applyPreconfigurationToRows}
            onDigitalPresetChange={(value) => (controller.digitalPreset = value)}
            onDigitalChannelChange={(channel, value) => {
              controller.digitalChannelChanges = {
                ...controller.digitalChannelChanges,
                [channel]: value,
              }
            }}
            onPostalPresetChange={(value) => (controller.postalPreset = value)}
          />
        {/snippet}

        {#snippet filters()}
          <BatchFilterBar
            fields={controller.filterFields}
            disabled={controller.tableLoading || controller.submissionPending || readOnly}
            onChange={controller.updateFilter}
          />
        {/snippet}

        {#snippet metrics()}
          <CostBenefitNumbersBlock
            estimate={controller.planningCostBenefit}
            loading={controller.costBenefitLoading}
            error={controller.costBenefitError}
            darkMode={true}
          />
          {#if controller.skippedPendingCount}
            <p class="contact-warning">{controller.skippedWarningText}</p>
          {/if}
        {/snippet}

        {#snippet footer()}
          {@render footerActions()}
        {/snippet}

        <ContactOrderPlanningTable
          columns={CONTACT_ORDER_APPOINTMENT_COLUMNS}
          rows={controller.tableRows}
          loading={controller.tableLoading}
          {readOnly}
          refreshKey={controller.tableLoading
            ? `contact-order-loading:${controller.tableRows.length}`
            : controller.appointmentsRefresh}
          disabled={controller.tableLoading ||
            controller.contactStepLocked ||
            controller.submissionPending}
          getDigitalChannelToggles={controller.digitalChannelTogglesForRow}
          getCommunicationSelection={controller.communicationSelectionForRow}
          getConfiguredPostalSelection={controller.configuredPostalSelectionForRow}
          getPostalOptions={controller.postalOptionsForRow}
          isAnnouncementPlanPending={controller.isAnnouncementPlanPending}
          selectedIds={controller.actionableSelectedIds}
          {rowPresentationByEventId}
          onRowsUpdaterReady={controller.bindRowsUpdater}
          onSelectionChange={(ids) => {
            controller.selectedIds = pruneContactOrderPlanningSelectedIds({
              selectedIds: ids,
              visibleRows: controller.visibleAppointmentRows,
              presentationByEventId: rowPresentationByEventId,
            })
          }}
          onToggleDigitalChannel={controller.toggleDigitalChannel}
          onSelectPostal={controller.selectPostal}
          onTogglePostal={controller.togglePostal}
          onRetryPreview={controller.retryPreview}
          onEditPostal={(row) => controller.openPostalEditor(row)}
          onDisablePlan={controller.disablePlanForRow}
          onEnablePlan={controller.enablePlanForRow}
          onRemoveRows={controller.disablePlansForRows}
          onResetRows={controller.resetPlansForRows}
          onEditRecipients={controller.openRecipientQuickEditForRow}
        />
      </TableStepLayout>
    {/snippet}
  </ContactOrderPlanningActions>
</div>

<style lang="less">
  .wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    width: 100%;
    min-width: 0;
  }

  .contact-warning {
    margin: -0.45rem 0 0;
    color: color-mix(in srgb, var(--text-invers-100) 78%, transparent);
    font-size: 0.88rem;
    line-height: 1.35;
  }
</style>
